/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback } from '@web-stories-wp/react';
import { applyVideoTrim, getVideoTrimRange } from '@web-stories-wp/media';

/**
 * Internal dependencies
 */
import { useConfig, useStory } from '../../app';
import VideoTrimContext from './videoTrimContext';
import useVideoTrimMode from './useVideoTrimMode';
import useVideoNode from './useVideoNode';

function VideoTrimProvider({ children }) {
  const animatedThumbnail = useConfig(
    ({ animatedThumbnail }) => animatedThumbnail
  );
  const { selectedElements, updateElementById } = useStory(
    ({ state: { selectedElements }, actions: { updateElementById } }) => ({
      selectedElements,
      updateElementById,
    })
  );
  const { isTrimMode, hasTrimMode, isAnimatedThumbnail, toggleTrimMode } =
    useVideoTrimMode();
  const savedTrim = getVideoTrimRange(selectedElements[0]?.resource);
  const {
    hasChanged,
    currentTime,
    isLooping,
    startOffset,
    endOffset,
    maxOffset,
    setStartOffset,
    setEndOffset,
    setIsLooping,
    setVideoNode,
    commitOffsets,
    resetOffsets,
  } = useVideoNode({
    fixedDurationMs: animatedThumbnail
      ? animatedThumbnail.fixedDurationSeconds * 1000
      : null,
    initialStartOffset: animatedThumbnail
      ? animatedThumbnail.initialStartSeconds * 1000
      : (savedTrim?.startSeconds ?? 0) * 1000,
    initialEndOffset:
      !animatedThumbnail && savedTrim ? savedTrim.endSeconds * 1000 : null,
    onDurationChange: animatedThumbnail?.onDurationChange,
  });

  const performTrim = useCallback(() => {
    if (isAnimatedThumbnail) {
      commitOffsets();
      animatedThumbnail.onTrim(startOffset / 1000);
      toggleTrimMode();
      return;
    }

    const [element] = selectedElements;
    if (hasChanged && element?.resource) {
      updateElementById({
        elementId: element.id,
        properties: {
          resource: applyVideoTrim(element.resource, {
            startMs: startOffset,
            endMs: endOffset,
            sourceLengthMs: maxOffset,
          }),
        },
      });
    }
    toggleTrimMode();
  }, [
    animatedThumbnail,
    commitOffsets,
    endOffset,
    hasChanged,
    isAnimatedThumbnail,
    maxOffset,
    selectedElements,
    startOffset,
    toggleTrimMode,
    updateElementById,
  ]);

  const cancelTrim = useCallback(() => {
    resetOffsets();
    toggleTrimMode();
  }, [resetOffsets, toggleTrimMode]);

  const value = {
    state: {
      hasChanged,
      isTrimMode,
      hasTrimMode,
      isAnimatedThumbnail,
      currentTime,
      isLooping,
      startOffset,
      endOffset,
      maxOffset,
    },
    actions: {
      performTrim,
      cancelTrim,
      toggleTrimMode,
      setVideoNode,
      setStartOffset,
      setEndOffset,
      setIsLooping,
      resetOffsets,
    },
  };

  return (
    <VideoTrimContext.Provider value={value}>
      {children}
    </VideoTrimContext.Provider>
  );
}

VideoTrimProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default VideoTrimProvider;
